'use strict';

const uuid = require('uuid');
const _ = {
    isEqual: require('lodash/isEqual'),
}

let VoiceStore,VoiceActions; // lazy load

const MESSAGE_TYPES = {
    // initial handshake
    request_system_data: "mzl-request-voice-system",
    system_data: 'mzl-voice-system-data',
    
    // incoming
    audio_url: 'mzl-voice-system-audio-url',
    stream_url: 'mzl-voice-system-stream-url',
    set_volume: 'mzl-voice-system-set-volume', 
    voice_element_mount_change: 'mzl-voice-element-mount-change',   // mount or unmount
    voice_element_play_change: 'mzl-voice-element-play-change',     // play started or stopped

    // outgoing
    audio_url_result: 'mzl-voice-system-audio-url-result',
    stream_url_result: 'mzl-voice-system-stream-url-result',
    options: 'mzl-voice-system-options',
    voice_element_command: 'mzl-voice-element-command',   // command to any listening voice elements ('play' or 'pause')
};

// utility class to help forge client communicate 
// with an app template in an iframe
// for polly voice synthesis
class VoiceSystem {
    constructor(props) {
        if (!VoiceStore) {
            VoiceStore = require("../stores/VoiceStore");
        }
        if (!VoiceActions) {
            VoiceActions = require("../actions/VoiceActions");
        }
        if (props.notAllowed) {
            this.notAllowed = true;
            this.options = { disabled: true };
            this.unsubscribes = [];
        }
        else {
            this.anonymous = props.anonymous;
            this.hasPolly = props.hasPolly;
            this.hasElevenLabs = props.hasElevenLabs;
            this.hasCartesia = props.hasCartesia;
            this.usageData = {
                channelId: props.channelId,
                activityId: props.activityId,
                shareId: props.shareId,
                shortCourseId: props.shortCourseId,
            };
            this.engines = props.engines;
            this.voices = props.voices;
            let { static_url, stream_url } = props.vs_funcs; // voice system functions
            this.fn_static = static_url;
            this.fn_stream = stream_url;
            this.unsubscribes = [VoiceStore.listen(this.onVoiceStoreChange)];
            let storeState = VoiceStore.getState();
            this.options = storeState.options;
            this.paused = !!storeState.voiceElement.paused;
        }
    }

    onVoiceStoreChange=(storeState)=>{
        let options = storeState.options;
        if (!_.isEqual(this.options,options)) {
            this.options = options;
            if (this.targetWindow) {
                this._sendOptions(this.targetWindow);
            }
        }
        if (storeState.action.type === 'setVoiceElementPlay') {
            this.paused = storeState.voiceElement.paused;
            if (this.targetWindow) {
                let play = !storeState.voiceElement.paused;
                this._sendPlayCommand(this.targetWindow, play);
            }
        }
    }
    
    _handleUrl(targetWindow,msg,isStream) {
        if (this.notAllowed) {
            console.error("VoiceSystem not allowed");
            return;
        }
        
        if (!targetWindow) {
            console.error("VoiceSystem needs target window");
            return;
        }
        this.targetWindow = targetWindow;
        let {request_id,engine,voice_id,phrase,save,cacheOnly,dynamic} = msg.data;
        
        // Notes on save,cacheOnly parameters (see redmine #7378)
        // save: specified for stream requests. indicates that the phrase should be saved to S3 also
        // cacheOnly: for non-stream.  Indicates that we only want to get the cached result, not synthesize anything 
        // (can be used right before a subsequent stream request for static phrases)
        // dynamic: for non-stream.  The phrase has the player name filled in (looked up via /cached, see VoiceStore.synthesizeAudio)

        let params = {request_id,engine,voice_id,phrase,save,cacheOnly,dynamic, usageData: this.usageData, anonymous: this.anonymous };
        //console.log(`_handleUrl isStream: ${isStream} params: ${JSON.stringify(params)}`);
        
        let type = isStream ? MESSAGE_TYPES.stream_url_result : MESSAGE_TYPES.audio_url_result;
        if (this.options.disabled) {
            const message = { type: type, data: { ... params, error: 'disabled'} };
            targetWindow.postMessage(message, '*');
            return;
        }

        let url_promise = isStream ? this.fn_stream : this.fn_static;
        
        url_promise(params)
        .then((url)=>{
            if (url && (url[0] === '/') && (url[1] !== '/')) {
                // return a *forge* url to the client
                url = GlobalConfig.ApiUrl + url;
            }
            var msg = { type: type, data: { ... params, url} };
            targetWindow.postMessage(msg, '*');
        });
    }

    _gotSystemData() {
        if (this.engines && this.voices) {
            return true;
        }
        if (!VoiceStore) {
            VoiceStore = require("../stores/VoiceStore");
        }
        let { engines,voices } = VoiceStore.getDataForVoiceSystem({
            anonymous: this.anonymous, 
            hasPolly: this.hasPolly, 
            hasElevenLabs: this.hasElevenLabs,
            hasCartesia: this.hasCartesia,
        });
        if (engines && voices) {
            this.engines = engines;
            this.voices = voices;
            return true;
        }
        return false;
    }
    
    _sendSystemData(targetWindow, retryCount) {
        if (!targetWindow) {
            console.error("VoiceSystem needs target window");
            return;
        }
        this.targetWindow = targetWindow;
        if (this.notAllowed) {
            targetWindow.postMessage({
                type: MESSAGE_TYPES.system_data,
                data: {
                    engines: [],
                    voices: [],
                    options: { disabled: true },
                    paused: false,
                }
            }, '*');
            return;
        }
        if (!this._gotSystemData()) {
            if (retryCount > 60) {
                console.error(`VoiceSystem giving up after ${retryCount} seconds`);
                return;
            }
            // console.info(`VoiceSystem waiting on system data ${retryCount}`);
            if (this.dataTimer) {
                // we are already in a loop requesting system data. Don't start it again.
                console.error("timer loop detected");
                return;
            }

            if (!retryCount) {
                retryCount = 1;
            }

            this.dataTimer = setTimeout(()=>{
                delete this.dataTimer;
                this._sendSystemData(targetWindow,retryCount + 1);
            },1000);
            return;
        }

        let data = {
            engines: this.engines,
            voices: this.voices,
            options: this.options,
            paused: this.paused,
            features: {
                dynamicCache: true, // phrases with the player name filled in can be looked up with dynamic (via /cached) and saved with stream&save (redmine #7650)
            },
        };
        targetWindow.postMessage({
            type: MESSAGE_TYPES.system_data,
            data
        }, '*');
    }
    
    _sendOptions(targetWindow) {
        targetWindow.postMessage({
            type: MESSAGE_TYPES.options,
            data: {
                options: this.options,
            }
        }, '*');
    }
    
    _sendPlayCommand(targetWindow,play) {
        targetWindow.postMessage({
            type: MESSAGE_TYPES.voice_element_command,
            data: {
                play, // boolean. 1=play, 0=pause
            }
        }, '*');
    }
    
    handleMessage(msg, targetWindow) {
        switch(msg.type) {
            case MESSAGE_TYPES.audio_url: 
                this._handleUrl(targetWindow, msg,false);
                return true;
            case MESSAGE_TYPES.stream_url: 
                this._handleUrl(targetWindow, msg,true);
                return true;
            case MESSAGE_TYPES.set_volume:
            {
                if (this.notAllowed) {
                    console.error("VoiceSystem not allowed");
                    return true;
                }
                let volume = msg.data.volume;
                let options = { ...this.options, volume };
                this.options = options; // so we don't detect a store change and send an unnecessary message to the child iframe
                VoiceActions.setVoiceOptions(uuid.v4(), options);
                return true;
            }
            case MESSAGE_TYPES.request_system_data:
                this._sendSystemData(targetWindow);
                return true;
                
            case MESSAGE_TYPES.voice_element_mount_change: // deprecated message
                // VoiceActions.setVoiceElementMounted(msg.data.mount);
                return true;
                
            case MESSAGE_TYPES.voice_element_play_change: // deprecated message
                // VoiceActions.setVoiceElementStartStop(uuid.v4(),msg.data);
                return true;
        }
        return false;
    }
    
    shutDown() {
        delete this.targetWindow;
        this.unsubscribes.forEach(u => u());
        if (this.dataTimer) {
            clearTimeout(this.dataTimer);
            delete this.dataTimer;
        }
    }

};

// outgoing message types
VoiceSystem.MESSAGE_TYPES = MESSAGE_TYPES;

module.exports = VoiceSystem;
